import React, { useState, useEffect, useCallback, useRef } from "react";
import Navbar from "./Navbar";
import Modal from "react-modal";
import { useNavigate, useParams } from "react-router-dom";
import Sortable from "sortablejs";
import { Container, Table, Row, Col, Button, Form, Card, ListGroup, Badge, FloatingLabel, Offcanvas, Modal as RBModal, Alert, Dropdown } from 'react-bootstrap';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { API_ENDPOINTS } from './config/api';
import PageTitle from './components/PageTitle';
import RichTextEditor from './components/RichTextEditor';
import { RATING_SCALE_TYPES, getRatingOptions, getRatingScaleTypeLabel } from "./utils/ratingScales";
import useNavigationWarning from './hooks/useNavigationWarning';
import NavigationWarningModal from './components/NavigationWarningModal';

Modal.setAppElement("#root");

const EvaluationBuilder = ({
  isEdit = false,
  isDuplicate = false,
  apiBase = API_ENDPOINTS.DIRECTOR,
  embedded = false,
  plainSidebar = false,
  skipInitialTemplateModal = false,
  saveRedirectPath,
}) => {
  const navigateOnSave = saveRedirectPath
    ?? (apiBase === API_ENDPOINTS.INSTITUTIONAL_ADMIN
      ? '/institutional-admin/evaluations'
      : '/evaluations');
  const [questions, setQuestions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formMeta, setFormMeta] = useState({
    title: "",
    type: "survey",
    evaluationType: "survey",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    assignedResidents: [],
    isTimed: false,
    timeLimit: 0,
    recurrence: "",
    isAnonymous: false,
    evaluatorType: "",
    selectedEvaluator: null,
    // Rich-text introduction shown at the top of the survey / evaluation
    introHtml: "",
    description: "",
  });
  const [residents, setResidents] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [evaluators, setEvaluators] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();
  const [showFieldOptions, setShowFieldOptions] = useState(false);
  const [residentSearch, setResidentSearch] = useState("");
  const [evaluatorSearch, setEvaluatorSearch] = useState("");
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [originalTemplates, setOriginalTemplates] = useState([]);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [hidePreviews, setHidePreviews] = useState(false);
  const [hasExistingEvaluations, setHasExistingEvaluations] = useState(false);
  const [radioMatrixActiveTab, setRadioMatrixActiveTab] = useState({});

  // Store initial state for navigation warning
  const initialQuestionsRef = useRef([]);
  const initialFormMetaRef = useRef({});
  // Use refs to keep originals for comparison (use deep equality check)
  useEffect(() => {
    initialQuestionsRef.current = JSON.parse(JSON.stringify(questions));
    initialFormMetaRef.current = JSON.parse(JSON.stringify(formMeta));
    // Only update when switching edit/duplicate/template or after save
    // eslint-disable-next-line
  }, [isEdit, isDuplicate, id]);

  // Determine if there are unsaved changes
  const hasUnsavedChanges =
    JSON.stringify(questions) !== JSON.stringify(initialQuestionsRef.current) ||
    JSON.stringify(formMeta) !== JSON.stringify(initialFormMetaRef.current);

  // Setup the navigation warning
  const navigationWarning = useNavigationWarning({
    isActive: true,
    evaluationData: { answers: questions, meta: formMeta }, // Proxy shape, the hook only cares about .answers having keys
    onSaveProgress: async () => {
      // Bring up Save Modal (showModal)
      setShowModal(true);
      // Allow navigation only after actual save is confirmed, so warn user in modal
      // To auto-proceed after save, you may add a callback to close the modal and then continue navigation
    },
    isTimed: false,
  });

  // Helper: get default columns for radio-matrix
  const getDefaultColumns = () => [
    "Unsatisfactory",
    "Needs Improvement",
    "Meets Expectations",
    "Exceeds Expectations"
  ];

  // Add/Edit columns for radio-matrix
  const updateMatrixColumn = (id, colIdx, value) => {
    setQuestions(questions.map(q =>
      q.id === id && q.type === "radio-matrix"
        ? { ...q, columns: q.columns.map((c, i) => i === colIdx ? value : c) }
        : q
    ));
  };
  const addMatrixColumn = (id) => {
    setQuestions(questions.map(q =>
      q.id === id && q.type === "radio-matrix"
        ? { ...q, columns: [...(q.columns || getDefaultColumns()), `New Column ${q.columns ? q.columns.length + 1 : 1}`] }
        : q
    ));
  };
  const removeMatrixColumn = (id, colIdx) => {
    setQuestions(questions.map(q =>
      q.id === id && q.type === "radio-matrix" && (q.columns || getDefaultColumns()).length > 1
        ? { ...q, columns: q.columns.filter((_, i) => i !== colIdx) }
        : q
    ));
  };

  // Add a ref to keep track of sortable column containers for each question
  const columnRefs = useRef({});

  // Helper to reorder columns in a radio-matrix question
  const reorderMatrixColumns = useCallback((questionId, fromIndex, toIndex) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) =>
        q.id === questionId && q.type === "radio-matrix"
          ? {
              ...q,
              columns: (() => {
                const newCols = [...(q.columns || getDefaultColumns())];
                const moved = newCols.splice(fromIndex, 1)[0];
                newCols.splice(toIndex, 0, moved);
                return newCols;
              })(),
            }
          : q
      )
    );
  }, [setQuestions]);

  // Initialize sortable for columns in Headers tab
  useEffect(() => {
    questions.forEach((q) => {
      if (q.type === "radio-matrix" && columnRefs.current[q.id]) {
        if (!columnRefs.current[q.id].sortableInstance) {
          columnRefs.current[q.id].sortableInstance = Sortable.create(columnRefs.current[q.id], {
            animation: 150,
            ghostClass: 'sortable-ghost',
            handle: '.matrix-col-drag-handle',
            onEnd: (evt) => {
              if (evt.oldIndex !== evt.newIndex) {
                reorderMatrixColumns(q.id, evt.oldIndex, evt.newIndex);
              }
            },
          });
        }
      }
    });
    // Cleanup on unmount
    return () => {
      Object.values(columnRefs.current).forEach(ref => {
        if (ref && ref.sortableInstance) {
          ref.sortableInstance.destroy();
          ref.sortableInstance = null;
        }
      });
    };
  }, [questions, reorderMatrixColumns]);


  // Duplicate question (works for all question types)
  const duplicateQuestion = (id) => {
    setQuestions((prevQuestions) => {
      const idx = prevQuestions.findIndex(q => q.id === id);
      if (idx === -1) return prevQuestions;
      const original = prevQuestions[idx];
      const copy = {
        ...original,
        id: Date.now() + Math.floor(Math.random() * 10000),
        ...(original.label ? { label: original.label + " (Copy)" } : {}),
        ...(original.header ? { header: original.header + " (Copy)" } : {}),
      };
      const newQuestions = [...prevQuestions];
      newQuestions.splice(idx + 1, 0, copy);
      return newQuestions;
    });
  };

useEffect(() => {
  if (isEdit || isDuplicate) {
    const token = localStorage.getItem("token");
    fetch(`${apiBase}/evaluation-templates/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        setQuestions(data.questions.map(q => {
          if (q.questionType === "header-paragraph") {
            try {
              const parsed = JSON.parse(q.questionText);
              return {
                ...q,
                header: parsed.header || "",
                paragraph: parsed.paragraph || "",
                size: parsed.size || "h2",
                type: q.questionType,
                id: q.id
              };
            } catch (e) {
              return {
                ...q,
                header: "",
                paragraph: "",
                size: "h2",
                type: q.questionType,
                id: q.id
              };
            }
          } else if (q.questionType === "radio-matrix") {
            return {
              ...q,
              label: q.label || q.questionText,
              type: q.type || q.questionType,
              options: q.options || [],
              columns: q.columns && q.columns.length > 0 ? q.columns : getDefaultColumns(),
              id: q.id
            };
          } else {
            return {
              ...q,
              label: q.label || q.questionText,
              type: q.type || q.questionType,
              options: q.options || [],
              id: q.id
            };
          }
        }));

        if (!isDuplicate) {
          setFormMeta({
            title: data.template.title,
            type: data.template.type,
            evaluationType: data.template.evaluationType,
            isTimed: data.template.isTimed,
            timeLimit: data.template.timeLimit,
            startDate: data.template.startTime.split("T")[0],
            startTime: new Date(data.template.startTime).toISOString().slice(11, 16),
            endDate: data.template.endTime.split("T")[0],
            endTime: new Date(data.template.endTime).toISOString().slice(11, 16),
            assignedResidents: data.template.assignedResidents || [],
            evaluatorType: data.template.evaluatorType || "",
            selectedEvaluator: data.template.evaluations?.[0]?.evaluatorId || null,
            recurrence: data.template.recurrence || "",
            isAnonymous: data.template.isAnonymous || false,
            introHtml: data.template.introHtml || "",
            description: data.template.description || ""
          });
          
          // Check if template has existing evaluations
          if (data.template.evaluations && data.template.evaluations.length > 0) {
            setHasExistingEvaluations(true);
          }
        }
      })
      .catch(error => {
        console.error("Error fetching template:", error);
      });
  }
}, [id, isEdit, isDuplicate, apiBase]);
  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${apiBase}/users`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setResidents(data.filter(user => user.role === 'resident'));
        setAssignableUsers(
          data.filter(user =>
            user.role === 'resident' || user.role === 'director' || user.role === 'attending'
          )
        );
        setEvaluators(data.filter(user => user.role === 'director' || user.role === 'attending'));
      });
  }, [apiBase]);
  
  const addQuestion = (type) => {
    if (type === "header-paragraph") {
      const newQuestion = {
        id: Date.now(),
        header: "",
        paragraph: "",
        type: "header-paragraph",
        size: "h2"
      };
      setQuestions([...questions, newQuestion]);
    } else if (type === "divider") {
      const newQuestion = {
        id: Date.now(),
        type: "divider",
        style: "solid"
      };
      setQuestions([...questions, newQuestion]);
    } else {
      const newQuestion = {
        id: Date.now(),
        label: "",
        type,
        options:
          type === "rating"
            ? { scaleType: RATING_SCALE_TYPES.NUMERIC_5, numericMax: 5 }
            : (type === "checkbox" || type === "radio" || type === "dropdown" || type === "radio-matrix" ? [""] : []),
        ...(type === "radio-matrix" ? { columns: getDefaultColumns() } : {}),
        // Optional helper text that can be shown before/after this question
        showInfo: false,
        infoPlacement: "after",
        infoHtml: ""
      };
      setQuestions([...questions, newQuestion]);
    }
  };

  const updateQuestion = (id, field, value) => {
    setQuestions(questions.map(q => q.id === id ? { ...q, [field]: value } : q));
  };

  const updateOption = (id, index, value) => {
    setQuestions(questions.map(q =>
      q.id === id
        ? {
            ...q,
            options: q.options.map((opt, i) => (i === index ? value : opt)),
          }
        : q
    ));
  };

  const addOption = (id) => {
    setQuestions(questions.map(q =>
      q.id === id
        ? { ...q, options: [...q.options, ""] }
        : q
    ));
  };

  const removeQuestion = (id) => {
    setQuestions(questions.filter(q => q.id !== id));
  };

  const removeOption = (id, index) => {
    setQuestions(questions.map(q =>
      q.id === id
        ? {
            ...q,
            options: q.options.filter((_, i) => i !== index),
          }
        : q
    ));
  };

  const reorderArray = useCallback((arr, fromIndex, toIndex) => {
    const newArr = [...arr];
    const movedItem = newArr.splice(fromIndex, 1)[0];
    newArr.splice(toIndex, 0, movedItem);
    return newArr;
  }, []);

  const initSortable = useCallback((el, questionId) => {
    if (!el) return;
    Sortable.create(el, {
      animation: 150,
      ghostClass: 'sortable-ghost',
      onEnd: (evt) => {
        const { oldIndex, newIndex } = evt;
        if (oldIndex !== newIndex) {
          setQuestions((prevQuestions) =>
            prevQuestions.map((q) =>
              q.id === questionId
                ? {
                    ...q,
                    options: reorderArray(q.options, oldIndex, newIndex),
                  }
                : q
            )
          );
        }
      },
    });
  }, [reorderArray]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    const programId = localStorage.getItem('programId');

    // Check for template saving first, before any other validation
    if (saveAsTemplate) {
      if (!formMeta.title) {
        alert("Please enter a template title");
        return;
      }

      // Force evaluationType to be template when saving as template
      const data = {
        title: formMeta.title,
        type: "template",
        evaluationType: "template", // Force this to be template
        introHtml: formMeta.introHtml || "",
        description: formMeta.description || "",
        startDate: new Date().toISOString().split('T')[0],
        startTime: new Date().toTimeString().slice(0, 5),
        endDate: new Date().toISOString().split('T')[0],
        endTime: new Date().toTimeString().slice(0, 5),
        assignedResidents: [], // Empty array for templates
        isTimed: false,
        timeLimit: null,
        recurrence: null,
        isAnonymous: false,
        evaluatorType: null,
        evaluatorId: null,
        questions: questions.map(q => {
          if (q.type === "header-paragraph") {
            return {
              id: q.id,
              label: JSON.stringify({
                header: q.header,
                paragraph: q.paragraph,
                size: q.size
              }),
              type: q.type
            };
          }
          return {
            id: q.id,
            label: q.label,
            type: q.type,
            options: q.options,
            columns: q.type === "radio-matrix" ? q.columns : null,
            infoHtml: q.infoHtml || "",
            infoPlacement: q.infoPlacement || null,
            showInfo: !!(q.showInfo || (q.infoHtml && q.infoHtml.trim() !== ""))
          };
        }),
        ...(programId ? { programId: parseInt(programId, 10) } : {})
      };

      try {
        const url = isEdit
          ? `${apiBase}/evaluation-templates/${id}`
          : `${apiBase}/evaluation-templates`;
        const response = await fetch(url, {
          method: isEdit ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(data),
        });

        if (response.ok) {
          setShowModal(false);
          navigate(navigateOnSave);
        } else {
          const error = await response.json();
          alert(error.message || "Failed to save template");
        }
      } catch (error) {
        console.error("Error saving template:", error);
        alert("Failed to save template");
      }
      return; // Early return to prevent any other validation
    }

    if (apiBase === API_ENDPOINTS.INSTITUTIONAL_ADMIN && !saveAsTemplate && !isEdit) {
      alert('Use "Save as template" in the save dialog when creating a new institution template.');
      return;
    }

    // Only proceed with regular evaluation validation if not saving as template
    if (!formMeta.title) {
      alert("Please enter an evaluation title");
      return;
    }

    if (formMeta.isTimed && (!formMeta.timeLimit || formMeta.timeLimit <= 0)) {
      alert("Please enter a valid time limit for timed evaluations");
      return;
    }

    // Validate evaluation type specific requirements
    if (formMeta.evaluationType === 'performance' || formMeta.evaluationType === 'feedback') {
      if (!formMeta.evaluatorType) {
        alert("Please select an evaluator type for " + formMeta.evaluationType + " evaluations");
        return;
      }
      if (!formMeta.selectedEvaluator) {
        alert("Please select an evaluator for " + formMeta.evaluationType + " evaluations");
        return;
      }
      if (formMeta.evaluationType === 'performance' && !formMeta.recurrence) {
        alert("Please select a recurrence for performance evaluations");
        return;
      }
    }

    // Check for assigned residents only for non-template evaluations
    if ((formMeta.evaluationType === 'survey' || formMeta.evaluationType === 'questionnaire') && 
        (!formMeta.assignedResidents || formMeta.assignedResidents.length === 0)) {
      alert("Please select at least one user for " + formMeta.evaluationType + " evaluation");
      return;
    }

    // Validate dates
    if (!formMeta.startDate || !formMeta.startTime || !formMeta.endDate || !formMeta.endTime) {
      alert("Please select both start and end dates and times");
      return;
    }

    // Create Date objects to validate
    const startDateTime = new Date(`${formMeta.startDate}T${formMeta.startTime}`);
    const endDateTime = new Date(`${formMeta.endDate}T${formMeta.endTime}`);

    if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
      alert("Please enter valid dates and times");
      return;
    }

    if (endDateTime <= startDateTime) {
      alert("End date and time must be after start date and time");
      return;
    }

    const data = {
      title: formMeta.title,
      type: formMeta.evaluationType,
      evaluationType: formMeta.evaluationType,
      introHtml: formMeta.introHtml || "",
      description: formMeta.description || "",
      startDate: formMeta.startDate,
      startTime: formMeta.startTime,
      endDate: formMeta.endDate,
      endTime: formMeta.endTime,
      assignedResidents: formMeta.assignedResidents,
      isTimed: formMeta.isTimed,
      timeLimit: formMeta.isTimed ? formMeta.timeLimit : null,
      recurrence: formMeta.evaluationType === 'performance' ? formMeta.recurrence : null,
      isAnonymous: formMeta.isAnonymous,
      evaluatorType: formMeta.evaluatorType,
      evaluatorId: formMeta.selectedEvaluator,
      questions: questions.map(q => {
        if (q.type === "header-paragraph") {
          return {
            id: q.id,
            label: JSON.stringify({
              header: q.header,
              paragraph: q.paragraph,
              size: q.size
            }),
            type: q.type
          };
        }
        return {
          id: q.id,
          label: q.label,
          type: q.type,
          options: q.options,
          columns: q.type === "radio-matrix" ? q.columns : null,
          infoHtml: q.infoHtml || "",
          infoPlacement: q.infoPlacement || null,
          showInfo: !!(q.showInfo || (q.infoHtml && q.infoHtml.trim() !== ""))
        };
      }),
      ...(programId ? { programId: parseInt(programId, 10) } : {})
    };

    console.log("Sending data to server:", data); // Add this line for debugging

    try {
      const url = isEdit
        ? `${apiBase}/evaluation-templates/${id}`
        : `${apiBase}/evaluation-templates`;

      const response = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Show success message with details about updates
        if (isEdit && result.updatedEvaluations !== undefined) {
          alert(`Evaluation template updated successfully!\n\n${result.updatedEvaluations} pending evaluations have been updated with the new times.`);
        } else {
          alert("Evaluation template saved successfully!");
        }
        
        setShowModal(false);
        navigate(navigateOnSave);
      } else {
        const error = await response.json();
        alert(error.message || "Failed to save evaluation");
      }
    } catch (error) {
      console.error("Error saving evaluation:", error);
      alert("Failed to save evaluation");
    }
  };

  const filteredResidents = residents.filter(r => {
    const search = residentSearch.toLowerCase();
    return (
      r.firstName.toLowerCase().includes(search) ||
      r.lastName.toLowerCase().includes(search) ||
      r.email.toLowerCase().includes(search)
    );
  });
  const filteredAssignableUsers = assignableUsers.filter((u) => {
    const search = residentSearch.toLowerCase();
    return (
      u.firstName.toLowerCase().includes(search) ||
      u.lastName.toLowerCase().includes(search) ||
      u.email.toLowerCase().includes(search)
    );
  });

  const allFilteredSelected = filteredResidents.length > 0 && filteredResidents.every(r => formMeta.assignedResidents.includes(r.id));
  const someFilteredSelected = filteredResidents.some(r => formMeta.assignedResidents.includes(r.id));

  const handleSelectAll = (checked) => {
    if (checked) {
      // Add all filtered residents
      setFormMeta(prev => ({
        ...prev,
        assignedResidents: Array.from(new Set([...prev.assignedResidents, ...filteredResidents.map(r => r.id)])),
      }));
    } else {
      // Remove all filtered residents
      setFormMeta(prev => ({
        ...prev,
        assignedResidents: prev.assignedResidents.filter(id => !filteredResidents.some(r => r.id === id)),
      }));
    }
  };

  // Add evaluation type options
  const evaluationTypes = [
    { value: "performance", label: "Performance Evaluation" },
    { value: "feedback", label: "Feedback Evaluation" },
    { value: "survey", label: "Survey" },
    { value: "questionnaire", label: "Questionnaire" }
  ];

  // Add recurrence options
  const recurrenceOptions = [
    { value: "monthly", label: "Monthly" },
    { value: "two_months", label: "Every Two Months" },
    { value: "three_months", label: "Quarterly" },
    { value: "four_months", label: "Every Four Months" },
    { value: "six_months", label: "Biannual" },
    { value: "yearly", label: "Annual" }
  ];

  // Add evaluator type options
  const evaluatorTypes = [
    { value: "director", label: "Program Director" },
    { value: "attending", label: "Attending Physician" }
  ];

  // Helper function to check if field should be shown based on evaluation type
  const shouldShowField = (field) => {
    switch (field) {
      case 'recurrence':
        return formMeta.evaluationType === 'performance';
      case 'isAnonymous':
        return formMeta.evaluationType === 'survey' || formMeta.evaluationType === 'questionnaire';
      case 'evaluatorType':
        return formMeta.evaluationType === 'performance' || formMeta.evaluationType === 'feedback';
      default:
        return true;
    }
  };

  // Filter evaluators based on selected type
  const filteredEvaluators = evaluators.filter(e => {
    const search = evaluatorSearch.toLowerCase();
    const matchesSearch = (
      e.firstName.toLowerCase().includes(search) ||
      e.lastName.toLowerCase().includes(search) ||
      e.email.toLowerCase().includes(search)
    );
    const matchesType = formMeta.evaluatorType === 'director' ? e.role === 'director' : e.role === 'attending';
    return matchesSearch && matchesType;
  });

  const openTemplatePicker = useCallback(async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${apiBase}/evaluation-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setTemplates(data);
      setOriginalTemplates(data);
      setShowTemplateModal(true);
    } catch (err) {
      console.error("Error fetching templates:", err);
    }
  }, [apiBase]);

  // Fetch templates if not editing or duplicating (director flow opens modal on load)
  useEffect(() => {
    if (!isEdit && !isDuplicate && !skipInitialTemplateModal) {
      openTemplatePicker();
    }
  }, [isEdit, isDuplicate, skipInitialTemplateModal, openTemplatePicker]);

  // State for selected template in dropdown
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  // Helper to load a template into the builder
  const loadTemplate = async (templateId) => {
    const token = localStorage.getItem("token");
    setShowTemplateModal(false);
    // Fetch template details
    const res = await fetch(`${apiBase}/evaluation-templates/${templateId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      setQuestions(data.questions.map(q => {
        if (q.questionType === "header-paragraph") {
          try {
            const parsed = JSON.parse(q.questionText);
            return {
              ...q,
              header: parsed.header || "",
              paragraph: parsed.paragraph || "",
              size: parsed.size || "h2",
              type: q.questionType,
              id: q.id
            };
          } catch (e) {
            return {
              ...q,
              header: "",
              paragraph: "",
              size: "h2",
              type: q.questionType,
              id: q.id
            };
          }
        } else if (q.questionType === "radio-matrix") {
          return {
            ...q,
            label: q.label || q.questionText,
            type: q.type || q.questionType,
            options: q.options || [],
            columns: q.columns && q.columns.length > 0 ? q.columns : getDefaultColumns(),
            id: q.id
          };
        } else {
          return {
            ...q,
            label: q.label || q.questionText,
            type: q.type || q.questionType,
            options: q.options || [],
            id: q.id
          };
        }
      }));
      // Reset form meta to default values, only keeping the title
      setFormMeta({
        title: data.template.title,
        type: "survey",
        evaluationType: "survey",
        startDate: "",
        startTime: "",
        endDate: "",
        endTime: "",
        assignedResidents: [],
        isTimed: false,
        timeLimit: 0,
        recurrence: "",
        isAnonymous: false,
        evaluatorType: "",
        selectedEvaluator: null,
        introHtml: data.template.introHtml || ""
      });
    }
  };

  // Handler for 'Load' button in modal
  const handleLoadTemplate = () => {
    if (selectedTemplateId) {
      loadTemplate(selectedTemplateId);
    }
  };

  // Handler for 'Start from scratch'
  const handleStartFromScratch = () => {
    setShowTemplateModal(false);
    setQuestions([]);
    setFormMeta({
      title: "",
      type: "survey",
      evaluationType: "survey",
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      assignedResidents: [],
      isTimed: false,
      timeLimit: 0,
      recurrence: "",
      isAnonymous: false,
      evaluatorType: "",
      selectedEvaluator: null,
      introHtml: "",
      description: "",
    });
  };

  const questionsContainerRef = useRef(null);
  const questionRefs = useRef({});
  const sidebarQuestionsListRef = useRef(null);
  const formDesignBodyStyle = embedded
    ? { maxHeight: 'calc(100vh - 13.5rem)', overflowY: 'auto' }
    : {};

  // Enable drag-and-drop for questions in sidebar
  useEffect(() => {
    if (sidebarQuestionsListRef.current && !sidebarQuestionsListRef.current.sortableInstance) {
      sidebarQuestionsListRef.current.sortableInstance = Sortable.create(sidebarQuestionsListRef.current, {
        animation: 150,
        ghostClass: 'sortable-ghost',
        handle: '.sidebar-question-drag-handle',
        onEnd: (evt) => {
          if (evt.oldIndex !== evt.newIndex) {
            setQuestions((prevQuestions) => {
              const newQuestions = [...prevQuestions];
              const [moved] = newQuestions.splice(evt.oldIndex, 1);
              newQuestions.splice(evt.newIndex, 0, moved);
              return newQuestions;
            });
          }
        },
      });
    }
    // Cleanup
    return () => {
      if (sidebarQuestionsListRef.current && sidebarQuestionsListRef.current.sortableInstance) {
        sidebarQuestionsListRef.current.sortableInstance.destroy();
        sidebarQuestionsListRef.current.sortableInstance = null;
      }
    };
  }, [questions]);

  return (
    <div className={embedded ? "h-100" : "min-vh-100"}>
      {!embedded && (
        <>
          <PageTitle title="Evaluation Builder" description="Create and customize evaluation forms and surveys" />
          <Navbar pageTitle="Create Evaluation Form" />
        </>
      )}
      <div className={embedded ? "border-bottom bg-inverse-light p-0 custom-sticky-top" : "bg-white shadow-sm mb-4 custom-sticky-top"}>
          <div className={embedded ? "container-fluid px-4" : "container"}>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex flex-row align-items-center justify-content-start gap-2">
              <h6 className="mb-0 fw-semibold py-3">Form Creation</h6>
              <span className="badge bg-secondary rounded-2 ms-2">
                {
                  questions.filter(q => q.type !== 'header-paragraph').length
                } Question{questions.filter(q => q.type !== 'header-paragraph').length !== 1 ? 's' : ''}
              </span>
              </div>
              <div className="d-flex flex-row align-items-center justify-content-end gap-2">
              {/* Load from Template button (only if not editing/duplicating) */}
              {!isEdit && !isDuplicate && (
                <Button variant="outline-primary" size="sm" onClick={() => openTemplatePicker()}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-folder"></i> <span className="d-none d-md-block ms-2">Load from Template</span>
                  </div>
                </Button>
              )}
              {embedded && questions.length > 0 && (
                <Button
                  variant={hidePreviews ? "light" : "outline-primary"}
                  size="sm"
                  onClick={() => setHidePreviews((prev) => !prev)}
                  className="ms-2"
                >
                  <div className="d-flex align-items-center">
                    <i className={`bi ${hidePreviews ? "bi-eye-slash" : "bi-eye"}`}></i>
                    <span className="d-none d-md-block ms-2">
                      {hidePreviews ? "Show Previews" : "Hide Previews"}
                    </span>
                  </div>
                </Button>
              )}
                 
              {questions.length > 0 && (
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => setShowModal(true)}
                          className="d-flex align-items-center gap-2"
                        >
                          <i className="bi bi-floppy2"></i> Save Form
                        </Button>
                      )}
              </div>
            </div>
            </div>
          </div>
      <Container fluid={embedded} className={embedded ? "p-0" : "mb-3"}>
        <Row className="justify-content-center">
          <Col xxl={12} xl={12} lg={12}>
            <Row className="g-4 align-items-stretch">
              {/* Sidebar - Questions List */}
              <Col lg={3} className={embedded ? "d-none d-lg-block bg-inverse-light p-0 border-end" : "d-none d-lg-block"}>
                <div className="position-sticky" style={{ top: '5rem' }}>
                  <div className={plainSidebar ? " px-4" : "card shadow-sm border-0 mb-3"}>
                    <div className={plainSidebar ? "px-2 fw-bold mb-2" : "card-header border-0 bg-white px-4 pt-3 fw-bold mb-2"}>
                      Questions
                    </div>
                    <div className={plainSidebar ? "px-1 pt-0" : "card-body px-3 pt-0"} style={{ maxHeight: 'calc(100vh - 20rem)', overflowY: 'auto' }}>
                      {questions.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          <i className="bi bi-inbox display-6 d-block mb-2"></i>
                          <small>No questions yet</small>
                        </div>
                      ) : (
                        <ul ref={sidebarQuestionsListRef} className="list-unstyled mb-0">
                          {questions.map((question, idx) => {
                            const questionNumber = questions
                              .slice(0, idx + 1)
                              .filter(q => !["header-paragraph", "divider"].includes(q.type))
                              .length;
                            const isHeaderOrDivider = ["header-paragraph", "divider"].includes(question.type);
                            const qId = question.id || `question-${idx}`;
                            
                            return (
                              <li key={qId} className="mb-1">
                                <div className="d-flex align-items-center bg-light p-1 rounded-3">
                                  <span className="sidebar-question-drag-handle text-muted" style={{cursor: 'grab', color: 'var(--gray)'}}>
                                    <i className="bi bi-grip-vertical"></i>
                                  </span>
                                  <Button
                                    variant="light"
                                    className="d-flex align-items-center justify-content-between p-2 rounded-3 border bg-white flex-grow-1"
                                    style={{ overflow: "hidden" }}
                                    onClick={() => {
                                      const node = questionRefs.current[qId];
                                      if (node) {
                                        node.scrollIntoView({
                                          behavior: "smooth",
                                          block: "center",
                                        });
                                      }
                                    }}
                                  >
                                    <div className="d-flex align-items-center gap-3 text-start">
                                      {!isHeaderOrDivider && (
                                        <div
                                          className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white"
                                          style={{ width: 26, height: 26, minWidth: 26 }}
                                        >
                                          <small className="fw-semibold">{questionNumber}</small>
                                        </div>
                                      )}
                                      <div>
                                        <div
                                          className="fw-semibold text-truncate small"
                                          style={{ maxWidth: 180 }}
                                        >
                                          {isHeaderOrDivider 
                                            ? (question.type === "header-paragraph" ? "Header & Paragraph" : "Divider")
                                            : (question.label || `Question ${questionNumber}`)
                                          }
                                        </div>
                                      </div>
                                    </div>
                                  </Button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </Col>

              {/* Form Design */}
              <Col lg={9} className={embedded ? "bg-inverse-light p-0 embeded-form-design-body-height" : ""}>
                <Card className={embedded ? "border-0 rounded-0" : "shadow-sm border-0 mb-3"} style={formDesignBodyStyle}>
                  <Card.Header className={embedded ? "d-none" : "border-bottom-0 px-md-4 border-20"} style={{backgroundColor: "var(--bs-card-bg)"}}>
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="fw-bold mb-0 py-3">Form Design</h6>
                      {/* Hide/Show Previews toggle */}
                      {questions.length > 0 && (
                        <Button
                          variant={hidePreviews ? "light" : "outline-primary"}
                          size="sm"
                          onClick={() => setHidePreviews((prev) => !prev)}
                          className="ms-2"
                        >
                          <div className="d-flex align-items-center">
                            <i className={`bi ${hidePreviews ? "bi-eye-slash" : "bi-eye"}`}></i> <span className="d-none d-md-block ms-2">{hidePreviews ? "Show Previews" : "Hide Previews"}</span>
                          </div>
                        </Button>
                      )}
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {/* Survey / evaluation introduction */}
                    <div className="px-4 pt-4 pb-3 border-bottom">
                      <h6 className="fw-semibold mb-1">Introduction</h6>
                      <p className="text-muted small mb-2">
                        Optional rich text that will appear at the top of this evaluation before any questions.
                      </p>
                      <RichTextEditor
                        value={formMeta.introHtml || ""}
                        onChange={(html) =>
                          setFormMeta((prev) => ({
                            ...prev,
                            introHtml: html,
                          }))
                        }
                        placeholder="Add introductory information about the purpose of this survey, instructions for respondents, or any disclaimers..."
                      />
                    </div>
                    {questions.length === 0 ? (
                      <div className="text-center p-5 m-4 mb-4 rounded bg-light" style={{border: "1px dashed var(--border-bg)"}}>
                        <i className="bi bi-plus-circle display-4 opacity-50 mt-5"></i>
                        <h5 className="mt-4">Add a field to create a question</h5>
                        <p className="text-muted">Click the "Add Field" button in the sidebar to get started</p>
                      </div>
                    ) : (
                      <div ref={questionsContainerRef}>
                        {questions.map((q, index) => {
                          const qId = q.id || `question-${index}`;
                          return (
                          <Card 
                            key={qId} 
                            className="border-0"
                            ref={(el) => {
                              if (el) {
                                questionRefs.current[qId] = el;
                              }
                            }}
                          >
                            <Card.Body className="p-md-4" style={{borderBottom: "1px dashed var(--border-bg)"}}>

                              {q.type === "header-paragraph" && (
                                
                                  <Row> 
                                  <Col lg={12} className="d-flex flex-row align-items-center justify-content-between mb-3">
                                 <div className="d-flex flex-row align-items-center">
                                  <h5 className="fs-6 mb-0">Header and Paragraph Block</h5></div>
                                  <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeQuestion(q.id)}
                                className="d-flex align-items-center gap-2"
                              >
                                <i className="bi bi-trash"></i> <span className="d-none d-md-inline ms-2">Remove</span>
                              </Button>
                                  </Col>
                                  <Col lg={10} md={10} sm={9} className="col-9">
                                  
                                  <Form.Control
                                    placeholder="Header Text"
                                    value={q.header}
                                    onChange={(e) => updateQuestion(q.id, "header", e.target.value)}
                                    className=" fs-6 mb-3 fw-bold border-1 bg-light"
                                  />
                                  </Col>
                                  <Col lg={2} md={2} sm={3} className="col-3">
                                  <Form.Select
                                    value={q.size || "h2"}
                                    onChange={(e) => updateQuestion(q.id, "size", e.target.value)}
                                  >
                                    <option value="h1">Large</option>
                                    <option value="h2">Medium</option>
                                    <option value="h3">Small</option>
                                  </Form.Select>
                                  </Col>
                                  <Col lg={12}>

                               
                                  <Form.Control
                                    as="textarea"
                                    placeholder="Paragraph Text"
                                    value={q.paragraph}
                                    onChange={(e) => updateQuestion(q.id, "paragraph", e.target.value)}
                                    rows={4}
                                  />
                                   </Col>
                                   </Row>
                                  
                              )}
                              

                              {q.type === "divider" && (
                                 <Row>
                                  <Col lg={12} className="d-flex flex-row align-items-center justify-content-between mb-3">
                                 <div className="d-flex flex-row align-items-center justify-content-center">
                                  <h5 className="fs-6 mb-0">Divider</h5></div>
                                  <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeQuestion(q.id)}
                                className="d-flex align-items-center gap-2"
                              >
                                <i className="bi bi-trash"></i> Remove
                              </Button>
                                  </Col>
                                  <Col lg={12}>
                                <Form.Select
                                  value={q.style || "solid"}
                                  onChange={(e) => updateQuestion(q.id, "style", e.target.value)}
                                >
                                  <option value="solid">Solid Line</option>
                                  <option value="dashed">Dashed Line</option>
                                  <option value="dotted">Dotted Line</option>
                                </Form.Select>
                                </Col>
                                </Row>
                              )}
                              

                              {!["header-paragraph", "divider"].includes(q.type) && (
                                <Row>
                                  <Col lg={12} className="d-flex  align-items-center justify-content-between mb-3"> 
                                  <div className="d-flex flex-row align-items-center gap-2 w-50">
                                  <small className="bg-secondary text-nowrap fw-semibold px-3 py-2 rounded-3">Question {index + 1}</small>
                                  {q.type === "radio-matrix" && (
                                    <div className="radio-inputs " role="group" aria-label="Radio matrix tabs" style={{maxWidth: '210px'}}> 
                                      <label className="radio" htmlFor={`tab-factors-${q.id}`}> 
                                        <input
                                        type="radio"
                                        name={`radio-matrix-tab-${q.id}`}
                                        id={`tab-factors-${q.id}`}
                                        checked={(radioMatrixActiveTab[q.id] || "factors") === "factors"}
                                        onChange={() => setRadioMatrixActiveTab({...radioMatrixActiveTab, [q.id]: "factors"})}
                                      /> <span className="name"><span className="d-none d-md-inline me-1">Matrix</span> Factors</span>
                                      </label>
                                      <label className="radio" htmlFor={`tab-headers-${q.id}`}> 
                                        <input
                                        type="radio"
                                        name={`radio-matrix-tab-${q.id}`}
                                        id={`tab-headers-${q.id}`}
                                        checked={(radioMatrixActiveTab[q.id] || "factors") === "headers"}
                                        onChange={() => setRadioMatrixActiveTab({...radioMatrixActiveTab, [q.id]: "headers"})}
                                      />
                                      <span className="name"><span className="d-none d-md-inline me-1">Edit</span> Headers</span>
                                      </label>
                                    </div>
                                  )}
                                 
                                  <span className={`${q.type === "radio-matrix" ? "d-none" : ""} small ms-2`}>{q.type}</span>
                                  </div>

                                  <div className="d-flex flex-row align-items-center gap-2">
                                    <Button
                                      variant="light"
                                      size="sm"
                                      title="Duplicate"
                                      onClick={() => duplicateQuestion(q.id)}
                                      className="d-flex align-items-center"
                                    >
                                      <i className="bi bi-files"></i>
                                    </Button>
                                    <Button
                                      variant={q.showInfo || (q.infoHtml && q.infoHtml.trim() !== "") ? "outline-secondary" : "outline-primary"}
                                      size="sm"
                                      title={q.showInfo || (q.infoHtml && q.infoHtml.trim() !== "") ? "Hide additional info" : "Add additional info"}
                                      onClick={() => {
                                        const shouldShow = !(q.showInfo || (q.infoHtml && q.infoHtml.trim() !== ""));
                                        setQuestions(prev =>
                                          prev.map(qq =>
                                            qq.id === q.id
                                              ? {
                                                  ...qq,
                                                  showInfo: shouldShow,
                                                  infoPlacement: qq.infoPlacement || "after",
                                                  infoHtml: shouldShow ? (qq.infoHtml || "") : ""
                                                }
                                              : qq
                                          )
                                        );
                                      }}
                                      className="d-flex align-items-center"
                                    >
                                      <i className="bi bi-card-text me-1"></i>
                                      <span className="d-none d-md-inline">{q.showInfo || (q.infoHtml && q.infoHtml.trim() !== "") ? "Info" : "Add Info"}</span>
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => removeQuestion(q.id)}
                                      className="d-flex align-items-center"
                                    >
                                      <i className="bi bi-trash"></i> <span className="d-none d-md-block ms-2">Remove</span>
                                    </Button>
                                  </div>

                                  </Col>
                                  <Col lg={12} md={12} sm={12} className="col-12">
                                <div className="position-relative">
                                  <Form.Control
                                    placeholder={`Question ${index + 1} label`}
                                    value={q.label}
                                    onChange={(e) => updateQuestion(q.id, "label", e.target.value)}
                                    style={{ paddingRight: '2.5rem' }}
                                  />
                                  {q.label && q.label.trim().length > 0 && (
                                    <Button
                                      variant="link-danger"
                                      size="sm"
                                      className="p-0 position-absolute top-50 translate-middle-y px-2" style={{right: '10px'}}
                                      onClick={() => updateQuestion(q.id, "label", "")}
                                      aria-label="Clear question label"
                                    >
                                      clear
                                    </Button>
                                  )}
                                </div>
                                </Col>
                                </Row>
                              )}



                              {/* Options for multiple-choice fields */}
                              {(q.type === "checkbox" || q.type === "radio" || q.type === "dropdown") && (
                                <div className="mt-3">
                                  <div ref={el => initSortable(el, q.id)}>
                                    {q.options.map((opt, i) => {
                                      const isLastOption = i === q.options.length - 1;
                                      return (
                                      <Row key={`${q.id}-option-${i}`}>
                                      <Col lg={12}>
                                      <div className="d-flex flex-row align-items-center mb-1 p-1 bg-light rounded-3 position-relative">
                                      <span className="question-drag-handle" style={{cursor: 'grab'}}>
                                        <i className="bi bi-grip-vertical text-muted"></i></span>
                                        
                                        <div className="flex-grow-1 position-relative">
                                          <Form.Control
                                            value={opt}
                                            onChange={(e) => updateOption(q.id, i, e.target.value)}
                                            placeholder={`Option ${i + 1}`}
                                            style={{paddingRight: isLastOption ? '120px' : '35px'}}
                                          />
                                          <Button
                                            variant="link-danger"
                                            aria-label="Remove option"
                                            className="position-absolute top-50 translate-middle-y p-0 px-1"
                                            style={{right: '8px'}}
                                            size="sm"
                                            onClick={() => removeOption(q.id, i)}
                                          >
                                            <i className="bi bi-x"></i>
                                          </Button>
                                        </div>
                                        {isLastOption && (
                                          <Button
                                            variant="outline-primary "
                                            size="sm ms-2"
                                            onClick={() => addOption(q.id)}
                                            className="flex-shrink-0"
                                          >
                                            <i className="bi bi-plus-lg"></i> <span className="d-none d-md-inline ms-1">Add Option</span>
                                          </Button>
                                        )}
                                      </div>
                                        </Col>
                                        </Row>
                                    )})}
                                  </div>
                                </div>
                              )}

                              {/* Radio Matrix with Tabs */}
                              {q.type === "radio-matrix" && (
                                <div className="mt-3">
                                  {(radioMatrixActiveTab[q.id] || "factors") === "factors" && (
                                    <div className="mt-3">
                                      <p className="text-muted mb-2">Add performance factors to be rated (drag to reorder):</p>
                                      <div ref={el => initSortable(el, q.id)}>
                                        {q.options.map((opt, i) => {
                                          const isLastOption = i === q.options.length - 1;
                                          return (
                                          <Row key={`${q.id}-option-${i}`}>
                                          <Col lg={12}>
                                          <div className="d-flex flex-row align-items-center mb-1 p-1 bg-light rounded-3 position-relative">
                                          <span className="question-drag-handle" style={{cursor: 'grab'}}>
                                            <i className="bi bi-grip-vertical text-muted"></i></span>
                                            
                                            <div className="flex-grow-1 position-relative">
                                              <Form.Control
                                                value={opt}
                                                onChange={(e) => updateOption(q.id, i, e.target.value)}
                                                placeholder={`Factor ${i + 1}`}
                                                style={{paddingRight: isLastOption ? '120px' : '35px'}}
                                              />
                                              <Button
                                                variant="link-danger"
                                                aria-label="Remove factor"
                                                className="position-absolute top-50 translate-middle-y p-0 px-1"
                                                style={{right: '8px'}}
                                                size="sm"
                                                onClick={() => removeOption(q.id, i)}
                                              >
                                                <i className="bi bi-x"></i>
                                              </Button>
                                            </div>
                                            {isLastOption && (
                                              <Button
                                                variant="outline-primary"
                                                size="sm ms-2"
                                                onClick={() => addOption(q.id)}
                                                className="flex-shrink-0"
                                              >
                                                <i className="bi bi-plus-lg"></i> <span className="d-none d-md-inline ms-1">Add Factor</span>
                                              </Button>
                                            )}
                                          </div>
                                            </Col>
                                            </Row>
                                        )})}
                                      </div>
                                    </div>
                                  )}
                                  {(radioMatrixActiveTab[q.id] || "factors") === "headers" && (
                                    <div className="mt-3">
                                      <p className="text-muted mb-2">Edit column headers for the performance matrix:</p>
                                      <div ref={el => {
                                        if (el) {
                                          columnRefs.current[q.id] = el;
                                        }
                                      }}>
                                        {(q.columns || getDefaultColumns()).map((col, colIdx) => {
                                          const isLastColumn = colIdx === (q.columns || getDefaultColumns()).length - 1;
                                          return (
                                            <Row key={`${q.id}-column-${colIdx}`}>
                                              <Col lg={12}>
                                                <div className="d-flex flex-row align-items-center mb-1 p-1 bg-light rounded-3 position-relative">
                                                  <span className="matrix-col-drag-handle" style={{cursor: 'grab'}}>
                                                    <i className="bi bi-grip-vertical text-muted"></i>
                                                  </span>
                                                  <div className="flex-grow-1 position-relative">
                                                    <Form.Control
                                                      value={col}
                                                      onChange={e => updateMatrixColumn(q.id, colIdx, e.target.value)}
                                                      placeholder={`Header ${colIdx + 1}`}
                                                      style={{paddingRight: isLastColumn ? '120px' : '35px'}}
                                                      isInvalid={
                                                        !col.trim() ||
                                                        (q.columns || getDefaultColumns()).filter((c, i) => c.trim() === col.trim() && i !== colIdx).length > 0
                                                      }
                                                    />
                                                    <Button
                                                      variant="link-danger"
                                                      aria-label="Remove column"
                                                      className="position-absolute top-50 translate-middle-y p-0 px-1"
                                                      style={{right: '8px'}}
                                                      size="sm"
                                                      onClick={() => removeMatrixColumn(q.id, colIdx)}
                                                      disabled={(q.columns || getDefaultColumns()).length <= 1}
                                                    >
                                                      <i className="bi bi-x"></i>
                                                    </Button>
                                                  </div>
                                                  {isLastColumn && (
                                                    <Button
                                                      variant="outline-primary"
                                                      size="sm ms-2"
                                                      onClick={() => addMatrixColumn(q.id)}
                                                      className="flex-shrink-0"
                                                    >
                                                      <i className="bi bi-plus-lg"></i> <span className="d-none d-md-inline ms-1">Add Column</span>
                                                    </Button>
                                                  )}
                                                </div>
                                              </Col>
                                            </Row>
                                          );
                                        })}
                                      </div>
                                      {/* Validation message */}
                                      {(() => {
                                        const cols = q.columns || getDefaultColumns();
                                        if (cols.some(c => !c.trim())) return <div className="text-danger small mt-2">No column can be empty.</div>;
                                        if (new Set(cols.map(c => c.trim())).size !== cols.length) return <div className="text-danger small mt-2">No duplicate column names allowed.</div>;
                                        return null;
                                      })()}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Rating scale configuration */}
                              {q.type === "rating" && (
                                <div className="mt-3">
                                  <div className="text-muted mt-2 mb-2">Rating scale</div>
                                  <Row className="g-2">
                                    <Col lg={8} md={8} sm={12}>
                                      <Form.Select
                                        value={(q.options && typeof q.options === "object" && !Array.isArray(q.options) ? q.options.scaleType : RATING_SCALE_TYPES.NUMERIC_5)}
                                        onChange={(e) => {
                                          const scaleType = e.target.value;
                                          const existing = (q.options && typeof q.options === "object" && !Array.isArray(q.options)) ? q.options : {};
                                          const next = { ...existing, scaleType };
                                          if (scaleType === RATING_SCALE_TYPES.NUMERIC_5) {
                                            next.numericMax = typeof next.numericMax === "number" ? next.numericMax : 5;
                                          } else {
                                            delete next.numericMax;
                                          }
                                          updateQuestion(q.id, "options", next);
                                        }}
                                      >
                                        {Object.values(RATING_SCALE_TYPES).map((t) => (
                                          <option key={t} value={t}>
                                            {getRatingScaleTypeLabel(t)}
                                          </option>
                                        ))}
                                      </Form.Select>
                                    </Col>
                                    <Col lg={4} md={4} sm={12}>
                                      {((q.options && typeof q.options === "object" && !Array.isArray(q.options) && q.options.scaleType) === RATING_SCALE_TYPES.NUMERIC_5) && (
                                        <Form.Select
                                          value={(q.options?.numericMax ?? 5)}
                                          onChange={(e) => {
                                            const numericMax = parseInt(e.target.value, 10);
                                            const existing = (q.options && typeof q.options === "object" && !Array.isArray(q.options)) ? q.options : {};
                                            updateQuestion(q.id, "options", { ...existing, numericMax });
                                          }}
                                        >
                                          {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                                            <option key={n} value={n}>{`1–${n}`}</option>
                                          ))}
                                        </Form.Select>
                                      )}
                                    </Col>
                                  </Row>
                                </div>
                              )}

                             

                              {/* Additional helper text for this question */}
                              {!["header-paragraph", "divider"].includes(q.type) && (q.showInfo || (q.infoHtml && q.infoHtml.trim() !== "")) && (
                                <div className="mt-4">
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Form.Label className="mb-0 small text-muted">
                                      Additional information for this question (optional)
                                    </Form.Label>
                                    <div className="d-flex align-items-center gap-2">
                                      <Form.Select
                                        size="sm"
                                        style={{ maxWidth: 260 }}
                                        value={q.infoPlacement || "after"}
                                        onChange={(e) =>
                                          updateQuestion(q.id, "infoPlacement", e.target.value)
                                        }
                                      >
                                        <option value="before">Show before question</option>
                                        <option value="after">Show after question</option>
                                      </Form.Select>
                                      <Button
                                        variant="link-danger"
                                        size="sm"
                                        className="p-0 px-2"
                                        title="Remove additional info"
                                        onClick={() => {
                                          setQuestions(prev =>
                                            prev.map(qq =>
                                              qq.id === q.id
                                                ? { ...qq, showInfo: false, infoHtml: "" }
                                                : qq
                                            )
                                          );
                                        }}
                                      >
                                        <i className="bi bi-x-lg"></i>
                                      </Button>
                                    </div>
                                  </div>
                                  <RichTextEditor
                                    value={q.infoHtml || ""}
                                    onChange={(html) => updateQuestion(q.id, "infoHtml", html)}
                                    placeholder="Add explanatory text, clarifications, or examples that should appear with this question."
                                  />
                                </div>
                              )}

                              {/* Preview section */}
                              {!hidePreviews && (
                                <Card className="mt-4 bg-light border-0">
                                  <Card.Body>
                                    <h6 className="text-muted mb-1 d-flex align-items-center">
                                      <i className="bi bi-eye me-2"></i> Preview
                                    </h6>
                                    {q.type === "header-paragraph" && (
                                      <div>
                                        <div className={`${q.size === "h1" ? "display-4" : q.size === "h2" ? "h2" : "h3"} mb-3`}>
                                          {q.header || "Header Text"}
                                        </div>
                                        <p className="mb-0">{q.paragraph || "Paragraph text goes here..."}</p>
                                      </div>
                                    )}
                                    {q.type === "divider" && (
                                      <hr className={`border-${q.style || "solid"}`} />
                                    )}
                                    {q.type === "text" && (
                                     
                                      <Form.Control
                                        type="text"
                                        placeholder="Single line text input"
                                        disabled
                                      />
                                    )}
                                    {q.type === "textarea" && (
                                      <Form.Control
                                        as="textarea"
                                        placeholder="Paragraph text input"
                                        disabled
                                        rows={4}
                                      />
                                    )}
                                    {q.type === "checkbox" && q.options.length > 0 && (
                                      <div>
                                        {q.options.map((opt, i) => (
                                          <Form.Check
                                            key={`${q.id}-preview-checkbox-${i}`}
                                            type="checkbox"
                                            label={opt || `Option ${i + 1}`}
                                            disabled
                                            className="mb-2"
                                          />
                                        ))}
                                      </div>
                                    )}
                                    {q.type === "radio" && q.options.length > 0 && (
                                      <div>
                                        {q.options.map((opt, i) => (
                                          <Form.Check
                                            key={`${q.id}-preview-radio-${i}`}
                                            type="radio"
                                            label={opt || `Option ${i + 1}`}
                                            disabled
                                            className="mb-2"
                                          />
                                        ))}
                                      </div>
                                    )}
                                    {q.type === "dropdown" && q.options.length > 0 && (
                                      <Form.Select disabled>
                                        <option value="">Select an option</option>
                                        {q.options.map((opt, i) => (
                                          <option key={`${q.id}-preview-dropdown-${i}`} value={opt}>{opt || `Option ${i + 1}`}</option>
                                        ))}
                                      </Form.Select>
                                    )}
                                    {q.type === "rating" && (
                                      <div className="d-flex flex-wrap gap-3 align-items-center">
                                        {getRatingOptions({ ...q, questionType: "rating" }).map((opt) => (
                                          <Form.Check
                                            key={`${q.id}-preview-rating-${String(opt.value)}`}
                                            type="radio"
                                            label={opt.label}
                                            disabled
                                          />
                                        ))}
                                      </div>
                                    )}
                                    {/* Preview for radio-matrix */}
                                    {q.type === "radio-matrix" && q.options.length > 0 && (
                                      <div className="table-responsive custom-table-responsive">
                                      <Table striped className="custom-border">
                                        <thead>
                                          <tr>
                                            <th style={{ width: "40%" }} className="px-3">Performance Factor</th>
                                            {(q.columns || getDefaultColumns()).map((col, colIdx) => (
                                              <th key={`${q.id}-preview-matrix-col-${colIdx}`} style={{ width: "15%" }}>{col}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {q.options.map((opt, idx) => (
                                            <tr key={`${q.id}-preview-matrix-row-${idx}`}>
                                              <td className="fw-medium px-3">{opt || `Factor ${idx + 1}`}</td>
                                              {(q.columns || getDefaultColumns()).map((_, colIdx) => (
                                                <td key={`${q.id}-preview-matrix-cell-${idx}-${colIdx}`} className="text-center">
                                                  <Form.Check type="radio" disabled />
                                                </td>
                                              ))}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </Table>
                                      </div>
                                    )}
                                  </Card.Body>
                                </Card>
                              )}
                            </Card.Body>
                          </Card>
                          );
                        })}
                      </div>
                    )}
                  </Card.Body>
                  </Card>
                  <Row>
                  <Col>
                    <Dropdown className="w-100 align-items-center" drop="up-centered" align="center">
                      <Dropdown.Toggle 
                        variant="link-dashed" 
                        className="d-flex align-items-center py-3 w-100 justify-content-center"
                        id="dropdown-field-options"
                      >
                        <i className="bi bi-plus-lg me-2"></i>
                        <span>Add Field</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {/*<Dropdown.Item onClick={() => addQuestion("header-paragraph")}>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-type-h1 text-primary"></i>
                            Header & Paragraph
                          </div>
                        </Dropdown.Item>*/}
                        <Dropdown.Item onClick={() => addQuestion("text")}>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-text-paragraph text-info"></i>
                            Single Answer
                          </div>
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => addQuestion("textarea")}>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-textarea-t text-success"></i>
                            Paragraph Answer
                          </div>
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => addQuestion("checkbox")}>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-check-square text-warning"></i>
                            Multiple Choice
                          </div>
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => addQuestion("radio")}>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-circle text-danger"></i>
                            Single Choice
                          </div>
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => addQuestion("radio-matrix")}>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-grid-3x3 text-success"></i>
                            Performance Matrix
                          </div>
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => addQuestion("dropdown")}>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-chevron-down text-dark"></i>
                            Select One
                          </div>
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => addQuestion("rating")}>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-star text-warning"></i>
                            Rating
                          </div>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                  </Row>  
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {/* Mobile Field Options Button */}
      {/*<div className="d-lg-none position-fixed bottom-0 end-0 p-4">
        <Button
          variant="primary"

          onClick={() => setShowFieldOptions(true)}
        >
          <i className="bi bi-plus-lg"></i>
          </Button>
        </div>

      {/* Mobile Field Options Offcanvas */}
       {/*<Offcanvas
        show={showFieldOptions}
        onHide={() => setShowFieldOptions(false)}
        placement="end"
        className="w-75"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Field Options</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <ListGroup variant="flush">
            <ListGroup.Item 
              action 
              onClick={() => addQuestion("header-paragraph")}
              className="d-flex align-items-center gap-2 py-3 hover-shadow"
            >
              <i className="bi bi-type-h1"></i>
              Header & Paragraph
            </ListGroup.Item>
           <ListGroup.Item 
              action 
              onClick={() => addQuestion("divider")}
              className="d-flex align-items-center gap-2 py-3 hover-shadow"
            >
              <i className="bi bi-hr"></i>
              Divider
            </ListGroup.Item>
            <ListGroup.Item 
              action 
              onClick={() => addQuestion("text")}
              className="d-flex align-items-center gap-2 py-3 hover-shadow"
            >
              <i className="bi bi-text-paragraph"></i>
              Single Line
            </ListGroup.Item>
            <ListGroup.Item 
              action 
              onClick={() => addQuestion("textarea")}
              className="d-flex align-items-center gap-2 py-3 hover-shadow"
            >
              <i className="bi bi-textarea-t"></i>
              Paragraph
            </ListGroup.Item>
            <ListGroup.Item 
              action 
              onClick={() => addQuestion("checkbox")}
              className="d-flex align-items-center gap-2 py-3 hover-shadow"
            >
              <i className="bi bi-check-square"></i>
              Checkboxes
            </ListGroup.Item>
            <ListGroup.Item 
              action 
              onClick={() => addQuestion("radio")}
              className="d-flex align-items-center gap-2 py-3 hover-shadow"
            >
              <i className="bi bi-circle"></i>
              Radio Buttons
            </ListGroup.Item>
            <ListGroup.Item 
              action 
              onClick={() => addQuestion("radio-matrix")}
              className="d-flex align-items-center gap-2 py-3 hover-shadow"
            >
              <i className="bi bi-grid-3x3"></i>
              Performance Matrix
            </ListGroup.Item>
            <ListGroup.Item 
              action 
              onClick={() => addQuestion("dropdown")}
              className="d-flex align-items-center gap-2 py-3 hover-shadow"
            >
              <i className="bi bi-chevron-down"></i>
              Dropdown
            </ListGroup.Item>
            <ListGroup.Item 
              action 
              onClick={() => addQuestion("rating")}
              className="d-flex align-items-center gap-2 py-3 hover-shadow"
            >
              <i className="bi bi-star"></i>
              Rating Scale
            </ListGroup.Item>
          </ListGroup>
        </Offcanvas.Body>
      </Offcanvas>*/}

      {/* Template Load Modal (blocking) */}
      <RBModal show={showTemplateModal} onHide={() => {}} centered size="lg" backdrop="static" keyboard={false}>
        <RBModal.Header className="d-flex flex-row justify-content-between align-items-center mb-3">
          <RBModal.Title className="mb-2 mb-md-0">
            Select Template
          </RBModal.Title>                
          <Form.Group className="mb-0 w-md-auto"> 
            <Form.Control
              type="text"
              placeholder="Search templates..."
              onChange={(e) => {
                const searchTerm = e.target.value.toLowerCase();
                if (!searchTerm) {
                  setTemplates(originalTemplates);
                } else {
                  const filtered = originalTemplates.filter(t =>
                    (t.evaluationType === 'template' || t.isInstitutionDefault) &&
                    t.title.toLowerCase().includes(searchTerm)
                  );
                  setTemplates(filtered);
                }
              }}
            />
          </Form.Group>
        </RBModal.Header>
        <RBModal.Body>
          {templates.filter(t => t.evaluationType === 'template' || t.isInstitutionDefault).length === 0 ? (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 gap-3"> <span className="text-muted">No templates available.</span> <Button variant="outline-primary" onClick={handleStartFromScratch}>Start from scratch</Button></div>
          ) : (
            <div>
              <ListGroup className="p-2 rounded-2 bg-light">
                {templates
                  .filter(t => t.evaluationType === 'template' || t.isInstitutionDefault)
                  .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))
                  .map((t) => (
                  <ListGroup.Item 
                    key={t.id}
                    onClick={() => setSelectedTemplateId(t.id)}
                    style={{ 
                      cursor: 'pointer',
                      backgroundColor: selectedTemplateId === t.id ? 'var(--bs-primary-bg-subtle)' : 'var(--card-bg)'
                    }}
                    className="d-flex border-0 align-items-center justify-content-between py-2"
                  >
                    <div className="d-flex align-items-center gap-3">
                      <Form.Check
                        type="checkbox"
                        checked={selectedTemplateId === t.id}
                        onChange={() => setSelectedTemplateId(t.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="d-flex flex-row justify-content-start align-items-start gap-3">
                        <span className="mb-0 fw-semibold">
                          {t.title}
                        </span>
                        <small className="text-muted">Created {new Date(t.createdAt).toLocaleDateString()}</small>
                      </div>
                    </div>
                    
                    {!t.isDefault && !t.isInstitutionDefault && (
                      
                      <Dropdown align="end" onClick={e => e.stopPropagation()}>
                        <Dropdown.Toggle 
                          variant="light-lite"
                          size="sm"
                          className="no-arrow px-2 py-1"
                          disabled={t.isDefault}
                          title={t.isDefault ? "Default templates cannot be deleted" : ""}
                          id={`template-action-dropdown-${t.id}`}
                        >
                          <i className="bi bi-three-dots-vertical"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item 
                            onClick={() => {
                              if (t.isDefault) {
                                alert("Default templates cannot be deleted. They are shared across all programs.");
                                return;
                              }
                              if (window.confirm('Are you sure you want to delete this template?')) {
                                const token = localStorage.getItem("token");
                                fetch(
                                  `${apiBase}/evaluation-templates/${t.id}`,
                                  {
                                    method: "DELETE",
                                    headers: { Authorization: `Bearer ${token}` },
                                  }
                                )
                                .then(response => {
                                  if (response.ok) {
                                    setTemplates(templates.filter(template => template.id !== t.id));
                                    setOriginalTemplates(originalTemplates.filter(template => template.id !== t.id));
                                    if (selectedTemplateId === t.id) {
                                      setSelectedTemplateId(null);
                                    }
                                  } else {
                                    return response.json().then(error => {
                                      throw new Error(error.message || "Failed to delete template");
                                    });
                                  }
                                })
                                .catch(error => {
                                  console.error("Error deleting template:", error);
                                  alert(error.message || "Failed to delete template");
                                });
                              }
                            }}
                            disabled={t.isDefault}
                          >
                            <i className="bi bi-trash me-2 text-danger"></i>
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>

              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button variant="secondary" size="sm" onClick={handleStartFromScratch}>
                  Start from scratch
                </Button>
                <Button 
                  variant="primary" 
                  size="sm"
                  onClick={() => {
                    if (selectedTemplateId) {
                      loadTemplate(selectedTemplateId);
                    }
                  }}
                  disabled={!selectedTemplateId}
                >
                  Load Selected Template
                </Button>
              </div>
            </div>
          )}
        </RBModal.Body>
      </RBModal>

      {/* Save Modal */}
      <RBModal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        backdrop="static"
        size="lg"
      >
        <RBModal.Header closeButton className="px-4 border-bottom-0">
            <RBModal.Title className="w-50"> 
              <div className="d-flex flex-column justify-content-start align-items-start">
                <span>Save Evaluation Form</span>
                <div className="text-hl"></div>
              </div>
          </RBModal.Title>
        </RBModal.Header>
        <RBModal.Body className="px-4">
          <Form>
            <Row>
              <Col lg={8}>
            <Form.Group className="mb-3">
              <FloatingLabel controlId="floatingInput" label="Evaluation Title" className="mb-3">
              <Form.Control
                placeholder="Enter evaluation title"
                value={formMeta.title}
                onChange={(e) => setFormMeta({ ...formMeta, title: e.target.value })}
              />
              </FloatingLabel>
            </Form.Group>
            </Col>
            <Col lg={4}>
            <Form.Group className="mb-3">
              <FloatingLabel controlId="floatingInput" label="Type of Evaluation" className="mb-3">
              <Form.Select
                value={saveAsTemplate ? "template" : formMeta.evaluationType}
                onChange={(e) => {
                  const newType = e.target.value;
                  setFormMeta({ 
                    ...formMeta, 
                    evaluationType: newType,
                    evaluatorType: (newType === 'survey' || newType === 'questionnaire') ? null : formMeta.evaluatorType
                  });
                }}
                disabled={saveAsTemplate}
              >
                {saveAsTemplate ? (
                  <option value="template">Template</option>
                ) : (
                  <>
                    <option value="performance">Performance Evaluation</option>
                    <option value="feedback">Feedback Evaluation</option>
                    <option value="survey">Survey</option>
                    <option value="questionnaire">Questionnaire</option>
                  </>
                )}
              </Form.Select>
              </FloatingLabel>
            </Form.Group>
            </Col>
           </Row>

            {!saveAsTemplate && (
              <>
                <Row>
                  <Col lg={3}>
                    <Form.Group>
                      <FloatingLabel controlId="floatingInput" label="Start Date" className="mb-3">
                      <Form.Control
                        type="date"
                        value={formMeta.startDate}
                        onChange={(e) => setFormMeta({ ...formMeta, startDate: e.target.value })}
                      />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col lg={3}>
                    <Form.Group>
                      <FloatingLabel controlId="floatingInput" label="Start Time" className="mb-3">
                      <Form.Control
                        type="time"
                        value={formMeta.startTime}
                        onChange={(e) => setFormMeta({ ...formMeta, startTime: e.target.value })}
                      />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>

                  <Col lg={3}>
                    <Form.Group>
                      <FloatingLabel controlId="floatingInput" label="End Date" className="mb-3">
                      <Form.Control
                        type="date"
                        value={formMeta.endDate}
                        onChange={(e) => setFormMeta({ ...formMeta, endDate: e.target.value })}
                      />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col lg={3}>
                    <Form.Group>
                      <FloatingLabel controlId="floatingInput" label="End Time" className="mb-3">
                      <Form.Control
                        type="time"
                        value={formMeta.endTime}
                        onChange={(e) => setFormMeta({ ...formMeta, endTime: e.target.value })}
                      />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                </Row>

           <Row>
             <Col lg={12}>
               <Form.Group className="mb-3">
                 <FloatingLabel controlId="floatingDescription" label="Description">
                   <Form.Control
                     as="textarea"
                     style={{ minHeight: "80px" }}
                     placeholder="Add a short description about this evaluation (purpose, context, or special instructions)."
                     value={formMeta.description}
                     onChange={(e) =>
                       setFormMeta((prev) => ({ ...prev, description: e.target.value }))
                     }
                   />
                 </FloatingLabel>
               </Form.Group>
             </Col>
           </Row>
                
                {/* Warning for existing evaluations */}
                {isEdit && hasExistingEvaluations && (
                  <Row>
                    <Col lg={12}>
                      <div className="alert alert-warning" role="alert">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        <strong>Note:</strong> This template has existing evaluations. 
                        Changing the start/end times will update all pending evaluations assigned to this template.
                      </div>
                    </Col>
                  </Row>
                )}
                
                {/* Conditional Fields */}
                {(formMeta.evaluationType === 'performance' || formMeta.evaluationType === 'feedback') && (
                  <>
                    <Row>
                      <Col lg={6} className="mb-2">
                        <Form.Group>
                          <FloatingLabel controlId="floatingInput" label="Evaluator Type">
                          <Form.Select
                            value={formMeta.evaluatorType || ''}
                            onChange={(e) => setFormMeta({ ...formMeta, evaluatorType: e.target.value })}
                          >
                            <option value="">Select evaluator type...</option>
                            <option value="attending">Attending</option>
                            <option value="director">Director</option>
                          </Form.Select>
                          </FloatingLabel>
                        </Form.Group>
                   </Col>
            
                {shouldShowField('recurrence') && (
                
                    <Col lg={6}>
                      <Form.Group>
                        <FloatingLabel controlId="floatingInput" label="Recurrence">
                        <Form.Select
                          value={formMeta.recurrence}
                          onChange={(e) => setFormMeta({ ...formMeta, recurrence: e.target.value })}
                        >
                          <option value="">Select Recurrence</option>
                          {recurrenceOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </Form.Select>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                )}     

                    {formMeta.evaluatorType && (
                        <Col lg={12} className="mt-2">
                          <Form.Group>
                            <Form.Label>
                              Select Evaluator
                              <span className="ms-2 text-primary small">
                                ({filteredEvaluators.length} available)
                              </span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Search evaluators by name or email..."
                              className="mb-2"
                              value={evaluatorSearch}
                              onChange={e => setEvaluatorSearch(e.target.value)}
                            />
                            <div className="border rounded p-3 bg-light" style={{ maxHeight: "200px", overflowY: "auto" }}>
                              {filteredEvaluators.length === 0 ? (
                                <div className="text-muted small">No evaluators found.</div>
                              ) : (
                                filteredEvaluators.map((e) => (
                                  <Form.Check
                                    key={e.id}
                                    type="radio"
                                    label={`${e.firstName} ${e.lastName} (${e.email})`}
                                    checked={formMeta.selectedEvaluator === e.id}
                                    onChange={() => {
                                      setFormMeta((prev) => ({
                                        ...prev,
                                        selectedEvaluator: e.id,
                                      }));
                                    }}
                                    disabled={(formMeta.evaluationType === 'performance' || formMeta.evaluationType === 'feedback') && formMeta.assignedResidents.includes(e.id)}
                                  />
                                ))
                              )}
                            </div>
                          </Form.Group>
                        </Col>
                  
                    )}
                    </Row>
                  </>
                )}


                
                <Row>
                {shouldShowField('isAnonymous') && (
                    <Col lg={4}>
                      <Form.Group className="pt-3 pb-3">
                        <Form.Check
                          type="checkbox"
                          label="Anonymous Responses"
                          checked={formMeta.isAnonymous}
                          onChange={(e) => setFormMeta({ ...formMeta, isAnonymous: e.target.checked })}
                        />
                      </Form.Group>
                    </Col>
                )}
                <Col lg={4}>
                <Form.Group className="pt-3 pb-3 d-flex align-items-center" style={{ margin: '4px 0 6px 0' }}>
                  <Form.Check
                    type="checkbox"
                    label="Timed Evaluation"
                    checked={formMeta.isTimed}
                    onChange={(e) => setFormMeta({ ...formMeta, isTimed: e.target.checked })}
                    className="form-switch"
                  />
                </Form.Group>
                </Col>
                <Col lg={4}>
                {formMeta.isTimed && (
                  <Form.Group>
                  
                    <FloatingLabel controlId="floatingInput" label="Time (min):">
                    <Form.Control
                      type="number"
                      placeholder="Enter time limit"
                      value={formMeta.timeLimit}
                      onChange={(e) => setFormMeta({ ...formMeta, timeLimit: parseInt(e.target.value) })}
                    />
                    </FloatingLabel>
                  </Form.Group>
                )}
                </Col>
                </Row>

                <Row>
                  <Col lg={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {formMeta.evaluationType === 'survey' || formMeta.evaluationType === 'questionnaire'
                          ? 'Select Users to Assign'
                          : 'Select Residents to Evaluate'}
                      </Form.Label>
                      {formMeta.evaluationType === 'survey' || formMeta.evaluationType === 'questionnaire' ? (
                        <>
                          <Form.Control
                            type="text"
                            placeholder="Search users by name or email..."
                            className="mb-2"
                            value={residentSearch}
                            onChange={(e) => setResidentSearch(e.target.value)}
                          />
                          <div className="border rounded p-3 bg-light" style={{ maxHeight: "200px", overflowY: "auto" }}>
                            <div className="mb-2">
                              <Form.Check
                                type="checkbox"
                                label="Select All"
                                checked={filteredAssignableUsers.length > 0 && filteredAssignableUsers.every(u => formMeta.assignedResidents.includes(u.id))}
                                ref={el => {
                                  if (el) el.indeterminate = !filteredAssignableUsers.every(u => formMeta.assignedResidents.includes(u.id)) && 
                                    filteredAssignableUsers.some(u => formMeta.assignedResidents.includes(u.id));
                                }}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormMeta(prev => ({
                                      ...prev,
                                      assignedResidents: Array.from(new Set([...prev.assignedResidents, ...filteredAssignableUsers.map(u => u.id)])),
                                    }));
                                  } else {
                                    setFormMeta(prev => ({
                                      ...prev,
                                      assignedResidents: prev.assignedResidents.filter(id => !filteredAssignableUsers.some(u => u.id === id)),
                                    }));
                                  }
                                }}
                                disabled={filteredAssignableUsers.length === 0}
                              />
                            </div>
                            {filteredAssignableUsers.length === 0 ? (
                              <div className="text-muted small">No users found.</div>
                            ) : (
                              filteredAssignableUsers.map((u) => (
                                <Form.Check
                                  key={u.id}
                                  type="checkbox"
                                  label={`${u.firstName} ${u.lastName} (${u.email}) - ${u.role}`}
                                  checked={formMeta.assignedResidents.includes(u.id)}
                                  onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    setFormMeta((prev) => ({
                                      ...prev,
                                      assignedResidents: isChecked
                                        ? [...prev.assignedResidents, u.id]
                                        : prev.assignedResidents.filter((id) => id !== u.id),
                                    }));
                                  }}
                                  className="mb-2"
                                />
                              ))
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="border rounded p-3 bg-light">
                          <div className="mb-2 text-muted small">
                            {/* For performance/feedback, these residents will be evaluated by the selected evaluator. Residents will NOT receive this evaluation as an assignment to fill out. */}
                            Select one or more residents to be evaluated. Only the selected evaluator (attending/director) will fill out the evaluation. Residents will see the results after completion.
                          </div>
                          <Form.Control
                            type="text"
                            placeholder="Search residents by name or email..."
                            className="mb-2"
                            value={residentSearch}
                            onChange={(e) => setResidentSearch(e.target.value)}
                          />
                          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                            <div className="mb-2">
                              <Form.Check
                                type="checkbox"
                                label="Select All"
                                checked={filteredResidents.length > 0 && filteredResidents.every(r => formMeta.assignedResidents.includes(r.id))}
                                ref={el => {
                                  if (el) el.indeterminate = !filteredResidents.every(r => formMeta.assignedResidents.includes(r.id)) && 
                                    filteredResidents.some(r => formMeta.assignedResidents.includes(r.id));
                                }}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormMeta(prev => ({
                                      ...prev,
                                      assignedResidents: Array.from(new Set([...prev.assignedResidents, ...filteredResidents.map(r => r.id)])),
                                    }));
                                  } else {
                                    setFormMeta(prev => ({
                                      ...prev,
                                      assignedResidents: prev.assignedResidents.filter(id => !filteredResidents.some(r => r.id === id)),
                                    }));
                                  }
                                }}
                                disabled={filteredResidents.length === 0}
                              />
                            </div>
                            {filteredResidents.length === 0 ? (
                              <div className="text-muted small">No residents found.</div>
                            ) : (
                              filteredResidents.map((r) => (
                                <Form.Check
                                  key={r.id}
                                  type="checkbox"
                                  label={`${r.firstName} ${r.lastName} (${r.email})`}
                                  checked={formMeta.assignedResidents.includes(r.id)}
                                  onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    setFormMeta((prev) => ({
                                      ...prev,
                                      assignedResidents: isChecked
                                        ? [...prev.assignedResidents, r.id]
                                        : prev.assignedResidents.filter((id) => id !== r.id),
                                    }));
                                  }}
                                  className="mb-2"
                                />
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Save as Template"
                checked={saveAsTemplate}
                onChange={e => {
                  const isChecked = e.target.checked;
                  setSaveAsTemplate(isChecked);
                  if (isChecked) {
                    setFormMeta(prev => ({
                      ...prev,
                      evaluationType: "template",
                      type: "template"
                    }));
                  } else {
                    setFormMeta(prev => ({
                      ...prev,
                      evaluationType: "survey",
                      type: "survey"
                    }));
                  }
                }}
              />
            </Form.Group>
          </Form>
        </RBModal.Body>
        <RBModal.Footer className="px-4 border-top-0">
          <Button
            variant="light"
            size="sm"
            onClick={() => setShowModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!formMeta.title}
            className="d-flex align-items-center gap-2"
          >
            <i className="bi bi-check-lg"></i> Confirm & Save
          </Button>
        </RBModal.Footer>
      </RBModal>

      {/* Navigation warning modal */}
      <NavigationWarningModal
        show={navigationWarning.showWarningModal}
        onHide={navigationWarning.handleCancelNavigation}
        onSaveAndLeave={() => {
          // For builder: only open the save modal; do not navigate
          setShowModal(true);
          navigationWarning.handleCancelNavigation();
        }}
        onLeaveWithoutSaving={navigationWarning.handleLeaveWithoutSaving}
        isTimed={false}
        timeLeft={0}
        saveButtonLabel="Save"
      />
    </div>
  );
};

export default EvaluationBuilder;
